<script>
import { mergeProps, normalizeClass, normalizeStyle } from 'vue';
import AutoComplete from './AutoComplete.vue';
import MobileSelect from './touchSelect.js';
import { countPickerOptions, touchPickerPolicyMixin } from './touchPickerPolicy.js';
import { createTouchPickerOptionSizer } from './touchPickerOptionSizing.js';

const passThroughValue = (value, options) => {
  const resolved = typeof value === 'function' ? value(options) : value;
  return typeof resolved === 'string' || Array.isArray(resolved)
    ? { class: resolved }
    : resolved || {};
};

// Touch-first devices use a choice list without summoning the keyboard. Other contexts
// retain the existing directly searchable field and scalar-value bindings.
export default {
  name: 'ShopManagementSelect',
  inheritAttrs: false,
  mixins: [touchPickerPolicyMixin],
  components: { AutoComplete, MobileSelect },
  props: {
    options: { type: Array, default: () => [] },
    remote: Boolean,
    // Keep custom selected values visible while searching inside the dropdown.
    filterInOverlay: Boolean,
    pt: { type: Object, default: () => ({}) },
    labelStyle: { type: [String, Object, Array], default: null },
    // These overlay-only options must not become HTML attributes on the
    // desktop autocomplete. Touch filtering follows the shared option threshold.
    filter: Boolean,
    filterIcon: { type: String, default: null },
    filterPlaceholder: { type: String, default: null },
    filterInputProps: { type: Object, default: null },
    autoFilterFocus: Boolean,
    clearIcon: { type: String, default: null },
  },
  data() {
    return {
      mobileOptionMinHeight: 0,
      mobileOptionSizer: createTouchPickerOptionSizer(),
    };
  },
  beforeUnmount() {
    this.mobileOptionSizer.unbind();
  },
  watch: {
    useMobileSelect() {
      this.handleMobileHide();
    },
  },
  computed: {
    useMobileSelect() {
      return this.useTouchPicker || (this.filterInOverlay && !this.remote);
    },
    touchPickerOptionCount() {
      return countPickerOptions(
        this.options,
        this.$attrs.optionGroupChildren || this.$attrs['option-group-children'],
      );
    },
    mobileSelectAttributes() {
      return {
        dropdownIcon: 'fa-solid fa-chevron-down p-icon',
        loadingIcon: 'fa-solid fa-spinner fa-spin p-icon',
        ...this.$attrs,
        options: this.options,
        labelStyle: normalizeStyle(this.labelStyle),
        filter: this.filterInOverlay ? this.filter : this.touchPickerFilter,
        filterIcon: this.filterIcon || 'fa-solid fa-magnifying-glass',
        filterPlaceholder: this.filterPlaceholder || 'Search',
        overlayClass: normalizeClass([
          this.$attrs.overlayClass || this.$attrs['overlay-class'],
          this.isTouchPickerContext && 'bayhive-touch-picker-overlay',
        ]),
        focusOnHover: false,
        autoFilterFocus: this.filterInOverlay && !this.isTouchPickerContext && this.autoFilterFocus,
        editable: false,
      };
    },
    mobileSelectPassThrough() {
      const required = this.$attrs.required;
      const ariaRequired =
        this.$attrs['aria-required'] ??
        this.$attrs.ariaRequired ??
        (required !== undefined && required !== null && required !== false ? true : undefined);
      const label = (options) =>
        mergeProps({ 'aria-required': ariaRequired }, passThroughValue(this.pt.label, options));
      if (!this.filterInputProps) return { ...this.pt, label };

      return {
        ...this.pt,
        label,
        pcFilter: (options) => {
          const filter = passThroughValue(this.pt.pcFilter, options);
          return {
            ...filter,
            root: (rootOptions) =>
              mergeProps(this.filterInputProps, passThroughValue(filter.root, rootOptions)),
          };
        },
      };
    },
    mobileVirtualScrollerOptions() {
      const options = this.$attrs.virtualScrollerOptions || this.$attrs['virtual-scroller-options'];
      if (!Number.isFinite(options?.itemSize) || this.mobileOptionMinHeight <= options.itemSize) {
        return options;
      }
      return { ...options, itemSize: this.mobileOptionMinHeight };
    },
    autocompleteAttributes() {
      return {
        ...this.$attrs,
        options: this.options,
        remote: this.remote,
        ...(this.isTouchPickerContext && this.remote
          ? {
              filterPlaceholder: this.filterPlaceholder,
              filterInputProps: this.filterInputProps,
              filterIcon: this.filterIcon,
            }
          : {}),
        selectedValueStyle: this.labelStyle,
        inputStyle: normalizeStyle([
          this.labelStyle,
          this.$attrs.inputStyle || this.$attrs['input-style'],
        ]),
      };
    },
    autocompletePassThrough() {
      const { label, pcInputText, ...other } = this.pt;

      if (!label) return this.pt;

      return {
        ...other,
        pcInputText: (options) => {
          const input = passThroughValue(pcInputText, options);
          return {
            ...input,
            root: (rootOptions) =>
              mergeProps(
                passThroughValue(label, rootOptions),
                passThroughValue(input.root, rootOptions),
              ),
          };
        },
      };
    },
    forwardedSlots() {
      return Object.keys(this.$slots).filter((name) => name !== 'clearicon');
    },
  },
  methods: {
    handleMobileShow() {
      this.$nextTick(() => {
        if (!this.mobileVirtualScrollerOptions) return;
        this.mobileOptionSizer.bind({
          overlay: this.overlayElement(),
          onSize: (height) => {
            this.mobileOptionMinHeight = height;
          },
        });
      });
    },
    handleMobileHide() {
      this.endTouchPickerSession();
      this.mobileOptionSizer.unbind();
      this.mobileOptionMinHeight = 0;
    },
    overlayElement() {
      if (this.useMobileSelect) return this.$refs.mobileSelect?.overlay || null;
      return this.$refs.autocomplete?.overlayElement?.() || null;
    },
    focus() {
      if (this.useMobileSelect) return this.$refs.mobileSelect?.$refs.focusInput?.focus();
      this.$refs.autocomplete?.inputElement()?.focus();
    },
    selectSingleFilteredOption(event) {
      if (!this.useMobileSelect) {
        return this.$refs.autocomplete?.selectSingleFilteredOption(event) || false;
      }
      const picker = this.$refs.mobileSelect;
      if (!picker?.overlayVisible || picker.disabled) return false;
      const option = picker.singleSelectableSuggestion();
      if (option === null) return false;

      event.preventDefault();
      event.stopPropagation();
      picker.selectOptionAndClose(event, option);
      return true;
    },
    show(...args) {
      if (this.useMobileSelect) return this.$refs.mobileSelect?.show(...args);
      return this.$refs.autocomplete?.show(...args);
    },
    hide(...args) {
      if (this.useMobileSelect) return this.$refs.mobileSelect?.hide(...args);
      return this.$refs.autocomplete?.hide(...args);
    },
  },
};
</script>

<template>
  <MobileSelect
    v-if="useMobileSelect"
    ref="mobileSelect"
    v-bind="mobileSelectAttributes"
    :pt="mobileSelectPassThrough"
    :virtual-scroller-options="mobileVirtualScrollerOptions"
    @before-show="beginTouchPickerSession"
    @show="handleMobileShow"
    @hide="handleMobileHide"
  >
    <template v-for="name in forwardedSlots" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps || {}" />
    </template>
    <template #clearicon="slotProps">
      <slot name="clearicon" v-bind="slotProps">
        <i
          :class="[slotProps.class, clearIcon || 'fa-solid fa-xmark']"
          aria-hidden="true"
          data-pc-section="clearicon"
          @click="slotProps.clearCallback"
        />
      </slot>
    </template>
  </MobileSelect>
  <AutoComplete
    v-else
    ref="autocomplete"
    v-bind="autocompleteAttributes"
    :pt="autocompletePassThrough"
  >
    <template v-for="name in forwardedSlots" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps || {}" />
    </template>
    <template #clearicon="slotProps">
      <slot name="clearicon" v-bind="slotProps">
        <i
          :class="[slotProps.class, clearIcon || 'fa-solid fa-xmark']"
          aria-hidden="true"
          data-pc-section="clearicon"
          @click="slotProps.clearCallback"
        />
      </slot>
    </template>
  </AutoComplete>
</template>
